// API shapes for /api/cpp-tests (mirror app/schemas/cpp_test.py).

export type CppMetric = "ipm" | "cpi" | "cpa" | "roas";
export type CppOnNoWinner = "keep" | "manual";

export type CppTestListItem = {
  id: number;
  name: string;
  app_id: number;
  app_name: string | null;
  campaign_id: number;
  metric: CppMetric;
  metric_day: number | null;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  group_count: number;
  variant_count: number;
  decided_count: number;
};

export type CppVariantSnapshot = {
  variant_id: number;
  display_name: string | null;
  product_page_id: string | null;
  is_default_page: boolean;
  ad_id: number | null;
  target_split_pct: number;
  impressions: number;
  taps: number;
  installs: number;
  spend_usd: number;
  revenue_usd: number;
  ipm: number | null;
  cpi: number | null;
  cpa: number | null;
  roas: number | null;
  fact: number | null;
  scaled_metric: number | null;
  diff_pct: number | null;
  ci_low: number | null;
  ci_high: number | null;
  /** Variant this one's bar is compared against; null on the baseline. */
  reference_variant_id: number | null;
  ci: number | null;
  state: "winner" | "trailing" | "collecting" | "early" | "nodiff";
  is_winner: boolean;
  days_remaining: number | null;
  /** C3: a CPP variant whose ASA ad_id isn't captured yet — awaiting its first
   * serve, not a real zero. Render a badge instead of 0s. */
  awaiting_delivery?: boolean;
  snapshot_ts: string | null;
  // Phase 2: per-variant cohort-by-day + per-event values, keyed by the
  // Ads-Manager column key (revenue_d7, roas_d7, trial_started, …).
  extra?: Record<string, number | null>;
};

export type CppGroupBlock = {
  group_id: number;
  ad_group_id: number;
  ad_group_name: string | null;
  status: string;
  outcome: string | null;
  winner_variant_id: number | null;
  /** Derived server-side: 'collecting' | 'in_progress' | 'winner_found'. */
  display_status: string;
  /** C2: one-line reason for the current display_status (why no bars yet). */
  status_reason?: string | null;
  forecast_days: number | null;
  /** 'estimating' | 'variants_close' | null — why there is no number. */
  forecast_note: string | null;
  winner_confirmed_at: string | null;
  variants: CppVariantSnapshot[];
};

export type CppTestDetail = {
  id: number;
  name: string;
  app_id: number;
  app_name: string | null;
  campaign_id: number;
  campaign_name: string | null;
  metric: CppMetric;
  metric_event: string | null;
  metric_day: number | null;
  confidence_level: number;
  min_uplift_pct: number;
  /** Frozen at test start: 'vs_default' | 'pairwise'. */
  comparison_mode: string;
  auto_apply: boolean;
  on_no_winner: CppOnNoWinner;
  stop_max_days: number | null;
  stop_spend_cap: number | null;
  status: string;
  started_at: string | null;
  ended_at: string | null;
  groups: CppGroupBlock[];
};

export type CppAvailableAdGroup = {
  ad_group_id: number;
  name: string | null;
  status: string | null;
  busy: boolean;
};

export type CppVariantIn = {
  product_page_id: string | null;
  creative_id: number | null;
  is_default_page: boolean;
  display_name: string | null;
  target_split_pct: number;
};

export type CppGroupIn = {
  ad_group_id: number;
  variants: CppVariantIn[];
};

export type CppCreateTestRequest = {
  app_id: number;
  campaign_id: number;
  name: string;
  metric: CppMetric;
  metric_event: string | null;
  metric_day: number | null;
  confidence_level: 90 | 95 | 99;
  min_uplift_pct: number;
  auto_apply: boolean;
  stop_max_days: number | null;
  stop_spend_cap: number | null;
  on_no_winner: CppOnNoWinner;
  groups: CppGroupIn[];
};

export type CppBatchCampaignIn = {
  campaign_id: number;
  ad_group_ids: number[];
};

// Multi-campaign create: shared config + variants, one test per campaign.
export type CppBatchCreateTestRequest = {
  app_id: number;
  name: string;
  metric: CppMetric;
  metric_event: string | null;
  metric_day: number | null;
  confidence_level: 90 | 95 | 99;
  min_uplift_pct: number;
  auto_apply: boolean;
  stop_max_days: number | null;
  stop_spend_cap: number | null;
  on_no_winner: CppOnNoWinner;
  variants: CppVariantIn[];
  campaigns: CppBatchCampaignIn[];
};

export type CppEstimateResponse = {
  estimated_days: number | null;
  daily_impressions: number | null;
  note: string;
};

export const CPP_COLS_DEFAULT = ["installs", "clicks"];

export const CPP_METRIC_META: Record<
  CppMetric,
  { short: string; label: string; impressionBased: boolean }
> = {
  ipm: { short: "IPM", label: "Installs per mille (installs / impressions)", impressionBased: true },
  cpi: { short: "CPI", label: "Cost per install", impressionBased: true },
  cpa: { short: "CPA", label: "Cost per in-app action", impressionBased: false },
  roas: { short: "ROAS", label: "Return on ad spend", impressionBased: false },
};
